/**
 * ReportPreviewSlot
 *
 * Renders a behaviour slot that fetches and displays a report preview.
 * Supports both FLAT and MULTI_PAGE report types, resolving the appropriate
 * blocks based on the report structure and action metadata.
 *
 * @param action - Agent experience action containing report metadata
 *   - `action.data.insight_id` - ID of the insight to fetch reports for
 *   - `action.data.page_group_id` - (MULTI_PAGE only) ID of the page group to display
 *   - `action.data.page_id` - (MULTI_PAGE only) ID of the specific page within the group
 *   - `action.data.title` - Optional title for the report (used in messages)
 * @param onUIAction - Callback for handling UI interactions within report blocks
 * @param readOnly - When true, interactive elements within blocks are disabled
 *
 * @remarks
 * **Data Flow:**
 * 1. Extracts `insight_id` from action.data
 * 2. Fetches reports via `useInsightReports` hook
 * 3. Resolves target blocks based on report type:
 *    - **FLAT**: Returns all blocks from `report.data.ui.blocks`
 *    - **MULTI_PAGE**: Resolves specific page using `resolveMultiPageBlocks`
 * 4. Renders blocks using `ReportBlocksRenderer`
 *
 * **States:**
 * - Loading: Shows skeleton loader
 * - Error/Empty: Shows "Unable to load report" message
 * - Success: Renders report blocks with expand button
 *
 * **Features:**
 * - Fullscreen expansion via portal to chat root
 */

import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { Maximize2 } from 'lucide-react';
import { LoadingSkeleton } from '@strivelabs/ui-blocks';
import type {
  BehaviourAgentExperienceAction,
  UIActionEvent,
  ReportBlock,
} from '@/types/ui-blocks';
import { isFlatReportData, isMultiPageReportData } from '@/types/ui-blocks';
import { ReportBlocksRenderer } from '../report-blocks-renderer';
import { useInsightReports } from '@/hooks/useInsights';
import { REPORT_TYPE } from '@/constants/reports';
import { FullscreenSlotOverlay, SlotContainer } from './shared';

/**
 * Resolves the target page's blocks from a MULTI_PAGE report payload.
 * Finds the PageGroup by page_group_id (falls back to first group),
 * then finds the Page by page_id (falls back to first page).
 */
function resolveMultiPageBlocks(
  pageGroups: Array<{ id: string; pages: Array<{ id?: string; blocks: ReportBlock[] }> }>,
  pageGroupId?: string,
  pageId?: string
): ReportBlock[] {
  const group = (pageGroupId
    ? pageGroups.find((g) => g.id === pageGroupId)
    : undefined) ?? pageGroups[0];

  if (!group) return [];

  const page = (pageId
    ? group.pages.find((p) => p.id === pageId)
    : undefined) ?? group.pages[0];

  return page?.blocks ?? [];
}

export interface ReportPreviewSlotProps {
  action: BehaviourAgentExperienceAction;
  onUIAction: (event: UIActionEvent) => void;
  readOnly?: boolean;
}

export function ReportPreviewSlot({
  action,
  onUIAction,
  readOnly,
}: ReportPreviewSlotProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);

  const insightId = action.data?.insight_id ?? '';
  const { data: reports, isLoading, isError } = useInsightReports(insightId, { enabled: !!insightId });

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  // Resolve blocks from the fetched reports.
  // Lookup priority:
  //   1. insight_report_id  → exact report match (works for FLAT & MULTI_PAGE)
  //   2. page_group_id      → search MULTI_PAGE reports for the matching group
  //   3. fallback           → reports[0]
  const resolvedBlocks: ReportBlock[] = (() => {
    if (!reports || reports.length === 0) return [];

    const targetReportId = action.data?.insight_report_id;
    const targetPageGroupId = action.data?.page_group_id;
    const targetPageId = action.data?.page_id;

    // 1. Direct report lookup by insight_report_id
    const targetReport = targetReportId
      ? reports.find((r) => String(r.id) === String(targetReportId))
      : undefined;

    // 2. If no direct match, search MULTI_PAGE reports by page_group_id
    const report = targetReport
      ?? (targetPageGroupId &&
        reports.find(
          (r) =>
            (r.type || REPORT_TYPE.FLAT) === REPORT_TYPE.MULTI_PAGE &&
            isMultiPageReportData(r.data) &&
            r.data.page_groups.some((g) => g.id === targetPageGroupId)
        ))
      // 3. Fallback to first report
      ?? reports[0];

    const reportType = report.type || REPORT_TYPE.FLAT;

    if (reportType === REPORT_TYPE.FLAT) {
      if (isFlatReportData(report.data)) {
        return report.data.ui.blocks;
      }
      return [];
    }

    if (isMultiPageReportData(report.data)) {
      return resolveMultiPageBlocks(
        report.data.page_groups,
        targetPageGroupId,
        targetPageId
      );
    }
    return [];
  })();

  // Loading state
  if (isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (isError || resolvedBlocks.length === 0) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <p className="text-sm text-muted-foreground">Unable to load report.</p>
        </div>
      </SlotContainer>
    );
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10 size-6 flex items-center justify-center rounded-md bg-white/60 border border-black/10 shadow-sm hover:bg-white/80 transition-colors"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </button>

        {/* Slot content */}
        <div className="p-10 space-y-7">
          <ReportBlocksRenderer blocks={resolvedBlocks} onUIAction={onUIAction} readOnly={readOnly} />
        </div>

      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            <ReportBlocksRenderer blocks={resolvedBlocks} onUIAction={onUIAction} readOnly={readOnly} />
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>

    </>
  );
}
